/**
 * services/web-ui/src/components/audit/AuditLogView.tsx
 *
 * Receipt-style scrollable list of all call events.
 *
 * Color-coding by event type namespace prefix:
 *   policy.* / consent.*          → orange
 *   safety.* / tripwire.*         → red
 *   tool.*                        → gray
 *   stage.*                       → blue
 *   stt.* / audio.* / confidence.*→ muted gray
 *   user.transcript               → light blue
 *   agent.utterance               → green
 *
 * Auto-scroll: follows new events unless user has scrolled up.
 * Selection sync: highlights rows in selected time range, auto-scrolls to first.
 */

import { useRef, useEffect, useCallback } from "react";
import type { SessionEvent } from "../../api/voice-sessions";

// ─── Type color mapping ───────────────────────────────────────────────────────

function getEventColor(type: string): string {
  if (type.startsWith("policy.") || type.startsWith("consent.") || type.startsWith("safety.")) {
    return "text-orange-400";
  }
  if (type.startsWith("tripwire.") || type.startsWith("stt.tripwire") || type === "policy.denied") {
    return "text-red-400";
  }
  if (type === "user.transcript") return "text-sky-400";
  if (type === "agent.utterance") return "text-green-400";
  if (type.startsWith("stage.")) return "text-blue-400";
  if (type.startsWith("stt.") || type.startsWith("audio.") || type.startsWith("confidence.")) {
    return "text-gray-500";
  }
  if (type.startsWith("tool.")) return "text-gray-400";
  return "text-gray-400";
}

function getEventBg(type: string, selected: boolean): string {
  if (selected) return "bg-amber-900/20 border-l-2 border-amber-500";
  if (type === "policy.denied" || type.startsWith("tripwire.")) return "bg-red-950/20";
  if (type === "user.transcript") return "bg-sky-950/20";
  if (type === "agent.utterance") return "bg-green-950/20";
  return "";
}

// ─── Payload summary ──────────────────────────────────────────────────────────

function summarizePayload(payload: Record<string, unknown>, type: string): string {
  if (type === "user.transcript" || type === "agent.utterance") {
    const text = payload["text"] ?? payload["transcript"] ?? "";
    if (typeof text === "string") return text.length > 70 ? text.slice(0, 69) + "…" : text;
  }
  if (type === "stage.entered") {
    const stage = payload["stage"] ?? "";
    const from = payload["from"] ?? payload["callId"] ?? "";
    return `→ ${stage}${from ? ` (from ${from})` : ""}`;
  }
  if (type === "tool.call") {
    const tool = payload["tool"] ?? "";
    const status = payload["status"] ?? "";
    return `${tool}${status ? ` [${status}]` : ""}`;
  }
  if (type === "policy.denied" || type.startsWith("policy.")) {
    const action = payload["action"] ?? payload["rule"] ?? "";
    return `action: ${action}`;
  }
  // Generic: first 2 key-value pairs
  const entries = Object.entries(payload)
    .filter(([k]) => !["session_id", "callId", "workflowId"].includes(k))
    .slice(0, 2)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(", ");
  return entries.length > 70 ? entries.slice(0, 69) + "…" : entries;
}

// ─── Time formatting ──────────────────────────────────────────────────────────

function formatRelativeTime(tsMs: number, startedAtMs: number): string {
  const elapsedMs = Math.max(0, tsMs - startedAtMs);
  const h = Math.floor(elapsedMs / 3_600_000);
  const m = Math.floor((elapsedMs % 3_600_000) / 60_000);
  const s = Math.floor((elapsedMs % 60_000) / 1000);
  const ms = elapsedMs % 1000;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
}

function formatAbsoluteTime(tsMs: number): string {
  return new Date(tsMs).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AuditSelection {
  ts_start_ms: number;
  ts_end_ms: number;
  source: "audit";
}

interface Props {
  events: SessionEvent[];
  startedAtMs: number;
  selection: { ts_start_ms: number; ts_end_ms: number } | null;
  onSelect(range: AuditSelection | null): void;
}

// ─── AuditLogView ─────────────────────────────────────────────────────────────

export function AuditLogView({ events, startedAtMs, selection, onSelect }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const firstSelectedRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on new events, unless user scrolled up
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
      userScrolledUp.current = !atBottom;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || userScrolledUp.current) return;
    el.scrollTop = el.scrollHeight;
  }, [events.length]);

  // Scroll to first selected row when selection changes from outside
  useEffect(() => {
    if (!selection || !firstSelectedRef.current) return;
    firstSelectedRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selection]);

  const isSelected = useCallback(
    (ev: SessionEvent): boolean => {
      if (!selection) return false;
      return ev.ts_ms >= selection.ts_start_ms && ev.ts_ms <= selection.ts_end_ms;
    },
    [selection],
  );

  const handleRowClick = (ev: SessionEvent) => {
    if (selection && ev.ts_ms >= selection.ts_start_ms && ev.ts_ms <= selection.ts_end_ms) {
      // Deselect if clicking inside current selection
      onSelect(null);
    } else {
      onSelect({
        ts_start_ms: ev.ts_ms,
        ts_end_ms: ev.ts_ms + 1,
        source: "audit",
      });
    }
  };

  let firstSelectedSet = false;

  return (
    <div className="flex flex-col h-full border border-gray-800 rounded-lg overflow-hidden bg-gray-950">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between shrink-0">
        <span className="text-xs text-gray-400 font-medium tracking-wide uppercase">
          Audit Log
        </span>
        <span className="text-xs text-gray-600">{events.length} events</span>
      </div>

      {/* Event list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        style={{ fontFamily: "monospace", fontSize: 11 }}
      >
        {events.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-600 text-xs">
            No events yet
          </div>
        ) : (
          events.map((ev, i) => {
            const selected = isSelected(ev);
            const color = getEventColor(ev.type);
            const bg = getEventBg(ev.type, selected);
            const summary = summarizePayload(ev.payload, ev.type);
            const relTime = formatRelativeTime(ev.ts_ms, startedAtMs);
            const absTime = formatAbsoluteTime(ev.ts_ms);

            let refProp: { ref: (el: HTMLDivElement | null) => void } | Record<string, never> = {};
            if (selected && !firstSelectedSet) {
              firstSelectedSet = true;
              refProp = {
                ref: (el: HTMLDivElement | null) => {
                  firstSelectedRef.current = el;
                },
              };
            }

            return (
              <div
                key={ev.id ?? i}
                {...refProp}
                className={`px-3 py-1 flex gap-2 items-baseline cursor-pointer hover:bg-gray-800/40 transition-colors ${bg}`}
                onClick={() => handleRowClick(ev)}
              >
                {/* Relative time */}
                <span
                  className="text-gray-600 shrink-0 select-none"
                  title={absTime}
                  style={{ minWidth: 84 }}
                >
                  {relTime}
                </span>

                {/* Event type */}
                <span className={`${color} shrink-0 font-semibold`} style={{ minWidth: 120 }}>
                  {ev.type.length > 20 ? ev.type.slice(0, 19) + "…" : ev.type}
                </span>

                {/* Payload summary */}
                <span className="text-gray-500 truncate flex-1">
                  {summary}
                </span>

                {/* Result indicator */}
                {ev.result_status && ev.result_status !== "success" && (
                  <span className={`shrink-0 text-xs ${ev.result_status === "denied" ? "text-red-400" : "text-yellow-400"}`}>
                    {ev.result_status}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
